import React, { createContext, useReducer, useContext, Dispatch } from "react";
import toast from "react-hot-toast";
import {
  getDelegateCalendarAppointments,
  getDelegateReps,
} from "services/delegateUserService";

export interface DelegateAppointment {
  id?: number;
  type: string;
  time: number;
  date: string;
  bookedByRep: number | null;
  clientId: number;
  confirmation: number | null;
  bookedByRepFirstName: string | null;
  bookedByRepLastName: string | null;
  annotationColor: string | null;
  annotationTitle: string | null;
  officeName: string | null;
}

export interface DelegateRepOfficeData {
  officeId: number;
  officeRequirement: boolean;
  userApprovalStatusWithOffice: "approved" | "denied" | "pending";
  userApprovalRequest: string | null;
  userBlockedStatusWithTheOffice: boolean;
  userBlockedTill: string | null;
  isIndefinateBlocked: boolean;
}

export interface DelegateRep {
  id: number;
  roleId: number;
  email: string;
  firstName: string;
  lastName: string;
  fileUrl: string | null;
  city: string | null;
  zip: string | null;
  name: string;
  url: string | null;
  phone: string | null;
  userProducts: JObj[];
  officeData?: DelegateRepOfficeData[];
}

export interface DelegateOffice {
  id: number;
  name: string;
  url: string;
  timezone: string;
  phone: string;
  lineOne: string;
  city: string;
  zip: string;
  firstName: string;
  lastName: string;
  fileUrl: string;
  email: string;
}

interface DelegateUserState {
  appointments: DelegateAppointment[];
  appointmentsLastFetchedAt: number | null;
  appointmentsIsLoading: boolean;
  reps: DelegateRep[];
  repsTotal: number;
  repsPage: number;
  repsLimit: number;
  repsLastFetchedAt: number | null;
  repsIsLoading: boolean;
  offices: DelegateOffice[];
  officesLastFetchedAt: number | null;
}

type Action =
  | { type: "SET_APPOINTMENTS"; payload: DelegateAppointment[] }
  | { type: "SET_APPOINTMENTS_LOADING"; payload: boolean }
  | {
      type: "SET_REPS";
      payload: {
        reps: DelegateRep[];
        total: number;
        page: number;
        limit: number;
      };
    }
  | { type: "SET_REPS_LOADING"; payload: boolean }
  | { type: "SET_OFFICES"; payload: DelegateOffice[] }
  | { type: "RESET" };

const initialState: DelegateUserState = {
  appointments: [],
  appointmentsLastFetchedAt: null,
  appointmentsIsLoading: false,
  reps: [],
  repsTotal: 0,
  repsPage: 1,
  repsLimit: 20,
  repsLastFetchedAt: null,
  repsIsLoading: false,
  offices: [],
  officesLastFetchedAt: null,
};

interface FetchDelegateAppointmentsParams {
  from: string;
  to: string;
}

interface FetchDelegateRepsParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: string;
  officeId?: number;
  showPending?: boolean;
  showApproved?: boolean;
  showDenied?: boolean;
  showBlocked?: boolean;
}

const DelegateUserContext = createContext<{
  state: DelegateUserState;
  dispatch: Dispatch<Action>;
  fetchDelegateAppointments: (
    params: FetchDelegateAppointmentsParams,
    refetch?: boolean,
  ) => Promise<void>;
  fetchDelegateReps: (
    params?: FetchDelegateRepsParams,
    refetch?: boolean,
  ) => Promise<void>;
  setOffices: (offices: DelegateOffice[]) => void;
} | null>(null);

function reducer(state: DelegateUserState, action: Action): DelegateUserState {
  switch (action.type) {
    case "SET_APPOINTMENTS":
      return {
        ...state,
        appointments: action.payload,
        appointmentsLastFetchedAt: Date.now(),
        appointmentsIsLoading: false,
      };
    case "SET_APPOINTMENTS_LOADING":
      return { ...state, appointmentsIsLoading: action.payload };
    case "SET_REPS":
      return {
        ...state,
        reps: action.payload.reps,
        repsTotal: action.payload.total,
        repsPage: action.payload.page,
        repsLimit: action.payload.limit,
        repsLastFetchedAt: Date.now(),
        repsIsLoading: false,
      };
    case "SET_REPS_LOADING":
      return { ...state, repsIsLoading: action.payload };
    case "SET_OFFICES":
      return {
        ...state,
        offices: action.payload,
        officesLastFetchedAt: Date.now(),
      };
    case "RESET":
      return initialState;
    default:
      return state;
  }
}

export const DelegateUserProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const fetchDelegateAppointments = async (
    { from, to }: FetchDelegateAppointmentsParams,
    refetch = false,
  ) => {
    try {
      const TEN_MINUTES = 10 * 60 * 1000;
      const shouldRefetch =
        refetch ||
        !state.appointmentsLastFetchedAt ||
        Date.now() - state.appointmentsLastFetchedAt > TEN_MINUTES;

      if (!shouldRefetch) return;

      dispatch({ type: "SET_APPOINTMENTS_LOADING", payload: true });

      const response = await getDelegateCalendarAppointments({ from, to });
      if (response?.error) {
        toast.error(response.error || "Failed to load delegate appointments");
        return;
      }

      dispatch({
        type: "SET_APPOINTMENTS",
        payload: response?.appointments || [],
      });
    } catch (error) {
      console.error("Error fetching delegate appointments:", error);
      toast.error("Error fetching delegate appointments");
    } finally {
      dispatch({ type: "SET_APPOINTMENTS_LOADING", payload: false });
    }
  };

  const fetchDelegateReps = async (
    params: FetchDelegateRepsParams = {},
    refetch = false,
  ) => {
    try {
      const TEN_MINUTES = 10 * 60 * 1000;
      const shouldRefetch =
        refetch ||
        !state.repsLastFetchedAt ||
        Date.now() - state.repsLastFetchedAt > TEN_MINUTES;

      if (!shouldRefetch) return;

      dispatch({ type: "SET_REPS_LOADING", payload: true });

      const response = await getDelegateReps(params);
      if (response?.error) {
        toast.error(response.error || "Failed to load delegate reps");
        return;
      }

      dispatch({
        type: "SET_REPS",
        payload: {
          reps: response?.users || [],
          total: response?.total || 0,
          page: response?.page || 1,
          limit: response?.limit || 20,
        },
      });
    } catch (error) {
      console.error("Error fetching delegate reps:", error);
      toast.error("Error fetching delegate reps");
    } finally {
      dispatch({ type: "SET_REPS_LOADING", payload: false });
    }
  };

  const setOffices = (offices: DelegateOffice[]) => {
    dispatch({ type: "SET_OFFICES", payload: offices });
  };

  return (
    <DelegateUserContext.Provider
      value={{
        state,
        dispatch,
        fetchDelegateAppointments,
        fetchDelegateReps,
        setOffices,
      }}
    >
      {children}
    </DelegateUserContext.Provider>
  );
};

export const useDelegateUserContext = () => {
  const context = useContext(DelegateUserContext);
  if (!context) {
    throw new Error(
      "useDelegateUserContext must be used within DelegateUserProvider",
    );
  }
  return context;
};
